import React, { useEffect, useState, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, fmtINR } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Package, ShoppingBag, Upload, Wallet, FileText, Download, ShieldCheck } from "lucide-react";
import { useSEO } from "../lib/useSEO";

const STATUSES = ["confirmed", "packed", "shipped", "delivered", "cancelled"];

export default function SellerDashboard() {
  useSEO({ title: "Seller Dashboard | Joy Pearl", description: "Manage your Joy Pearl seller catalogue, orders, payouts and KYC.", noIndex: true });
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [kyc, setKyc] = useState(null);
  const [payouts, setPayouts] = useState({ rows: [], summary: {} });

  useEffect(() => {
    if (!user) return;
    api.get(`/products?seller_id=${user.user_id}&limit=200`).then((r) => setProducts(r.data));
    api.get("/seller/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/seller/kyc").then((r) => setKyc(r.data)).catch(() => setKyc({ status: "pending" }));
    api.get("/finance/payouts").then((r) => {
      // filter to this seller
      const rows = (r.data.rows || []).filter((x) => x.seller_id === user.user_id);
      setPayouts({ rows, summary: rows[0] ? { gross: rows[0].gross, commission: rows[0].commission, net: rows[0].net_payable } : {} });
    }).catch(() => {});
  }, [user]);

  if (loading) return <div className="jp-container py-20 text-center text-brand-muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "seller" && user.role !== "admin") return (
    <div className="jp-container py-20 text-center">
      <h1 className="font-serif text-3xl mb-3">Seller access only</h1>
      <Link to="/account" className="jp-btn-primary mt-4 inline-flex">Apply to be a seller</Link>
    </div>
  );
  if (user.role === "seller" && user.seller_status !== "approved") return (
    <div className="jp-container py-20 text-center" data-testid="seller-pending">
      <h1 className="font-serif text-3xl mb-3">Seller application: <span className="capitalize">{user.seller_status}</span></h1>
      <p className="text-brand-muted">An admin will review your application shortly.</p>
    </div>
  );

  const reloadProducts = () => api.get(`/products?seller_id=${user.user_id}&limit=200`).then((r) => setProducts(r.data));
  const reloadOrders = () => api.get("/seller/orders").then((r) => setOrders(r.data));

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); reloadProducts(); }
    catch { toast.error("Could not delete"); }
  };
  const updateStatus = async (oid, status) => {
    try { await api.post(`/seller/orders/${oid}/status`, { status }); toast.success(`Order ${status}`); reloadOrders(); }
    catch { toast.error("Could not update"); }
  };

  return (
    <div className="jp-container py-8 md:py-12" data-testid="seller-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="jp-eyebrow mb-2">Seller</p>
          <h1 className="font-serif text-3xl md:text-5xl">{user.seller_info?.business_name || user.name}</h1>
        </div>
      </div>

      <div className="border-b border-brand-line mb-8 flex gap-1 overflow-x-auto">
        <button onClick={() => setTab("products")} className={`px-4 py-3 text-sm uppercase tracking-wider whitespace-nowrap ${tab === "products" ? "text-brand-primary border-b-2 border-brand-primary" : "text-brand-muted"}`} data-testid="tab-products">
          <Package size={14} className="inline mr-2"/>Products ({products.length})
        </button>
        <button onClick={() => setTab("orders")} className={`px-4 py-3 text-sm uppercase tracking-wider whitespace-nowrap ${tab === "orders" ? "text-brand-primary border-b-2 border-brand-primary" : "text-brand-muted"}`} data-testid="tab-orders">
          <ShoppingBag size={14} className="inline mr-2"/>Orders ({orders.length})
        </button>
        <button onClick={() => setTab("payouts")} className={`px-4 py-3 text-sm uppercase tracking-wider whitespace-nowrap ${tab === "payouts" ? "text-brand-primary border-b-2 border-brand-primary" : "text-brand-muted"}`} data-testid="tab-payouts">
          <Wallet size={14} className="inline mr-2"/>Payouts
        </button>
        <button onClick={() => setTab("kyc")} className={`px-4 py-3 text-sm uppercase tracking-wider whitespace-nowrap ${tab === "kyc" ? "text-brand-primary border-b-2 border-brand-primary" : "text-brand-muted"}`} data-testid="tab-kyc">
          <ShieldCheck size={14} className="inline mr-2"/>KYC {kyc?.status && <span className="jp-badge ml-2 capitalize">{kyc.status}</span>}
        </button>
      </div>

      {tab === "products" && (
        <>
          {kyc && kyc.status !== "approved" && (
            <div className="mb-4 p-4 border border-amber-300 bg-amber-50 text-sm text-amber-900" data-testid="kyc-warning">
              Your KYC is <strong className="capitalize">{kyc.status}</strong>. Add Product is disabled until approved. <button onClick={() => setTab("kyc")} className="underline">Submit KYC →</button>
            </div>
          )}
          <div className="flex justify-end gap-3 mb-4">
            <button onClick={() => setShowBulk(true)} className="jp-btn-outline" data-testid="bulk-upload-btn"><Upload size={14} className="mr-2"/>Bulk Upload</button>
            <button onClick={() => { setEditing(null); setShowForm(true); }} disabled={kyc && kyc.status !== "approved"} className="jp-btn-primary" data-testid="add-product-btn"><Plus size={14} className="mr-2"/>Add Product</button>
          </div>
          {showBulk && <BulkUpload onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); reloadProducts(); }}/>}
          {showForm && <ProductForm categories={categories} editing={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); reloadProducts(); }}/>}
          <div className="bg-white border border-brand-line">
            <table className="w-full text-sm">
              <thead className="bg-brand-blush/30">
                <tr>
                  <th className="text-left p-3 font-medium">Product</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Category</th>
                  <th className="text-right p-3 font-medium">Price</th>
                  <th className="text-right p-3 font-medium">Stock</th>
                  <th className="text-right p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr><td colSpan={5} className="text-center p-12 text-brand-muted">No products yet. Add your first product to get started.</td></tr>
                ) : products.map((p) => (
                  <tr key={p.id} className="border-t border-brand-line" data-testid={`seller-product-row-${p.id}`}>
                    <td className="p-3 flex items-center gap-3">
                      <div className="h-10 w-10 bg-brand-blush overflow-hidden">{p.images?.[0] && <img src={p.images[0]} alt="" className="h-full w-full object-cover"/>}</div>
                      <span className="font-medium">{p.name}</span>
                    </td>
                    <td className="p-3 hidden md:table-cell text-brand-muted">{p.category_name}</td>
                    <td className="p-3 text-right">{fmtINR(p.price)}</td>
                    <td className="p-3 text-right">{p.stock}</td>
                    <td className="p-3 text-right">
                      <button onClick={() => { setEditing(p); setShowForm(true); }} className="text-brand-primary text-xs uppercase tracking-wider mr-3">Edit</button>
                      <button onClick={() => deleteProduct(p.id)} className="text-red-700 inline-flex items-center"><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "orders" && (
        <div className="bg-white border border-brand-line">
          <table className="w-full text-sm">
            <thead className="bg-brand-blush/30">
              <tr>
                <th className="text-left p-3 font-medium">Order</th>
                <th className="text-left p-3 font-medium hidden md:table-cell">Customer</th>
                <th className="text-right p-3 font-medium">Total</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-right p-3 font-medium">Update</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr><td colSpan={5} className="text-center p-12 text-brand-muted">No orders yet.</td></tr>
              ) : orders.map((o) => (
                <tr key={o.id} className="border-t border-brand-line" data-testid={`seller-order-${o.id}`}>
                  <td className="p-3">
                    <p className="font-medium">{o.order_number}</p>
                    <p className="text-xs text-brand-muted">{new Date(o.created_at).toLocaleDateString("en-IN")}</p>
                    <a href="#" onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const r = await api.get(`/orders/${o.id}/packing-slip-pdf`, { responseType: "blob" });
                        const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `JoyPearl-PackingSlip-${o.order_number}.pdf`;
                        a.click();
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                      } catch { toast.error("Could not generate packing slip"); }
                    }} className="text-xs text-brand-primary hover:underline inline-flex items-center mt-1" data-testid={`packing-slip-${o.id}`}><FileText size={11} className="mr-1"/>Packing slip (PDF)</a>
                  </td>
                  <td className="p-3 hidden md:table-cell">{o.customer_name}<br/><span className="text-xs text-brand-muted">{o.customer_phone}</span></td>
                  <td className="p-3 text-right">{fmtINR(o.total)}</td>
                  <td className="p-3"><span className="jp-badge capitalize">{o.status}</span></td>
                  <td className="p-3 text-right">
                    <select onChange={(e) => updateStatus(o.id, e.target.value)} defaultValue="" className="text-xs border border-brand-line p-1" data-testid={`order-status-${o.id}`}>
                      <option value="" disabled>Change…</option>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "payouts" && <SellerPayoutsView payouts={payouts} orders={orders}/>}
      {tab === "kyc" && <SellerKyc kyc={kyc} onUpdated={(k) => setKyc(k)}/>}
    </div>
  );
}

function ProductForm({ categories, editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing ? {
    name: editing.name, description: editing.description, category_id: editing.category_id,
    price: editing.price, mrp: editing.mrp, stock: editing.stock, images: editing.images || [], tags: editing.tags || [],
    variants: editing.variants || [],
  } : { name: "", description: "", category_id: categories[0]?.id || "", price: "", mrp: "", stock: 1, images: [], tags: [], variants: [] });
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const addImageUrl = () => { if (imageUrl) { setForm({...form, images: [...form.images, imageUrl]}); setImageUrl(""); } };
  const removeImage = (i) => setForm({...form, images: form.images.filter((_, idx) => idx !== i)});

  const addVariant = () => setForm((f) => ({ ...f, variants: [...(f.variants || []), { sku: "", color: "", size: "", material: "", additional_price: 0, stock: 1 }] }));
  const updateVariant = (i, key, val) => setForm((f) => {
    const copy = [...(f.variants || [])];
    copy[i] = { ...copy[i], [key]: val };
    return { ...f, variants: copy };
  });
  const removeVariant = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));

  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${api.defaults.baseURL}/files/${data.path}`;
      setForm((f) => ({...f, images: [...f.images, url]}));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanVariants = (form.variants || [])
        .filter((v) => (v.color || v.size || v.material))
        .map((v) => ({
          sku: v.sku || "",
          color: v.color || "",
          size: v.size || "",
          material: v.material || "",
          additional_price: parseFloat(v.additional_price || 0),
          stock: parseInt(v.stock || 0),
        }));
      const totalStock = cleanVariants.length
        ? cleanVariants.reduce((s, v) => s + (parseInt(v.stock) || 0), 0)
        : parseInt(form.stock || 0);
      const payload = {
        ...form, price: parseFloat(form.price), mrp: parseFloat(form.mrp), stock: totalStock,
      };
      delete payload.variants;
      let pid;
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        pid = editing.id;
      } else {
        const { data } = await api.post("/products", payload);
        pid = data.id;
      }
      // Sync variants (set them after create/update)
      if (pid) {
        await api.post(`/products/${pid}/variants`, cleanVariants);
      }
      toast.success(editing ? "Updated" : "Product created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-brand-line p-6 mb-6" data-testid="product-form">
      <h3 className="font-serif text-xl mb-4">{editing ? "Edit Product" : "Add New Product"}</h3>
      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input required placeholder="Product name" className="jp-input sm:col-span-2" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="pf-name"/>
        <textarea placeholder="Description" className="jp-input sm:col-span-2 min-h-[80px]" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/>
        <select required className="jp-input" value={form.category_id} onChange={(e) => setForm({...form, category_id: e.target.value})} data-testid="pf-category">
          <option value="">Select category…</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input required type="number" min={1} placeholder="Stock" className="jp-input" value={form.stock} onChange={(e) => setForm({...form, stock: e.target.value})}/>
        <input required type="number" step="0.01" min={0} placeholder="Price (₹)" className="jp-input" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} data-testid="pf-price"/>
        <input required type="number" step="0.01" min={0} placeholder="MRP (₹)" className="jp-input" value={form.mrp} onChange={(e) => setForm({...form, mrp: e.target.value})}/>

        <div className="sm:col-span-2">
          <label className="jp-eyebrow">Images</label>
          <div className="flex flex-wrap gap-3 mt-3">
            {form.images.map((img, i) => (
              <div key={i} className="relative h-20 w-20 border border-brand-line">
                <img src={img} alt="" className="h-full w-full object-cover"/>
                <button type="button" onClick={() => removeImage(i)} className="absolute -top-2 -right-2 h-6 w-6 bg-white border border-brand-line rounded-full text-xs flex items-center justify-center">×</button>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex gap-2">
              <input placeholder="Paste image URL" className="jp-input flex-1" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)}/>
              <button type="button" onClick={addImageUrl} className="jp-btn-outline px-3 py-2 text-xs">Add</button>
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" onChange={uploadFile} className="hidden" id="pf-file"/>
              <label htmlFor="pf-file" className="jp-btn-outline inline-flex cursor-pointer w-full justify-center"><Upload size={14} className="mr-2"/>{uploading ? "Uploading…" : "Upload from device"}</label>
            </div>
          </div>
        </div>

        <div className="sm:col-span-2 border-t border-brand-line pt-4 mt-2" data-testid="variants-section">
          <div className="flex items-center justify-between">
            <div>
              <label className="jp-eyebrow">Variants (Size · Color · Material)</label>
              <p className="text-xs text-brand-muted mt-1">Optional. If added, customers must pick one. Per-variant stock overrides the base stock.</p>
            </div>
            <button type="button" onClick={addVariant} className="jp-btn-outline px-3 py-2 text-xs" data-testid="add-variant-btn"><Plus size={12} className="mr-1"/>Add variant</button>
          </div>
          {(form.variants || []).length > 0 && (
            <div className="mt-3 space-y-2">
              {form.variants.map((v, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`variant-row-${i}`}>
                  <input placeholder="Size" className="jp-input col-span-2 text-sm" value={v.size || ""} onChange={(e) => updateVariant(i, "size", e.target.value)} data-testid={`variant-size-${i}`}/>
                  <input placeholder="Color" className="jp-input col-span-2 text-sm" value={v.color || ""} onChange={(e) => updateVariant(i, "color", e.target.value)} data-testid={`variant-color-${i}`}/>
                  <input placeholder="Material" className="jp-input col-span-2 text-sm" value={v.material || ""} onChange={(e) => updateVariant(i, "material", e.target.value)} data-testid={`variant-material-${i}`}/>
                  <input placeholder="+₹ extra" type="number" step="0.01" className="jp-input col-span-2 text-sm" value={v.additional_price || 0} onChange={(e) => updateVariant(i, "additional_price", e.target.value)}/>
                  <input placeholder="Stock" type="number" min={0} className="jp-input col-span-2 text-sm" value={v.stock ?? 0} onChange={(e) => updateVariant(i, "stock", e.target.value)} data-testid={`variant-stock-${i}`}/>
                  <button type="button" onClick={() => removeVariant(i)} className="col-span-1 text-red-700" aria-label="Remove" data-testid={`remove-variant-${i}`}><Trash2 size={14}/></button>
                  <input placeholder="SKU (optional)" className="jp-input col-span-12 text-xs" value={v.sku || ""} onChange={(e) => updateVariant(i, "sku", e.target.value)}/>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sm:col-span-2 flex gap-3 mt-2">
          <button type="submit" disabled={saving} className="jp-btn-primary" data-testid="pf-save">{saving ? "Saving…" : (editing ? "Update" : "Create Product")}</button>
          <button type="button" onClick={onClose} className="jp-btn-outline">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function SellerKyc({ kyc, onUpdated }) {
  const [uploading, setUploading] = useState("");
  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/seller/kyc/upload/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated(data.kyc); toast.success(`${kind.toUpperCase()} uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(""); }
  };
  const docs = [
    { k: "pan", label: "PAN Card", url: kyc?.pan_url },
    { k: "gstin", label: "GSTIN Certificate", url: kyc?.gstin_url },
    { k: "cheque", label: "Cancelled Cheque", url: kyc?.cheque_url },
  ];
  return (
    <div className="bg-white border border-brand-line p-6 max-w-2xl" data-testid="seller-kyc">
      <h3 className="font-serif text-xl mb-2">KYC Verification</h3>
      <p className="text-sm text-brand-muted mb-4">Status: <span className="capitalize font-medium text-brand-ink">{kyc?.status || "pending"}</span>{kyc?.reason && <span className="block text-xs mt-1">Reason: {kyc.reason}</span>}</p>
      <div className="space-y-4">
        {docs.map((d) => (
          <div key={d.k} className="border border-brand-line p-3 flex items-center gap-3" data-testid={`kyc-${d.k}`}>
            <FileText size={18} className="text-brand-primary"/>
            <div className="flex-1">
              <p className="font-medium text-sm">{d.label}</p>
              {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-primary hover:underline">View uploaded</a>}
            </div>
            <label className="jp-btn-outline cursor-pointer px-3 py-2 text-xs">
              <input type="file" accept="image/*,.pdf" onChange={(e) => upload(d.k, e.target.files?.[0])} className="hidden"/>
              {uploading === d.k ? "Uploading…" : (d.url ? "Replace" : "Upload")}
            </label>
          </div>
        ))}
      </div>
      <p className="text-xs text-brand-muted mt-4">All documents are reviewed by Joy Pearl admin within 1-2 business days. You can list products only after KYC approval.</p>
    </div>
  );
}

function SellerPayoutsView({ payouts, orders }) {
  const delivered = orders.filter((o) => o.status === "delivered");
  return (
    <div data-testid="seller-payouts">
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[["Gross", payouts.summary?.gross], ["Commission", payouts.summary?.commission], ["Net Payable", payouts.summary?.net]].map(([l, v]) => (
          <div key={l} className="bg-white border border-brand-line p-4"><p className="text-xs text-brand-muted uppercase">{l}</p><p className="font-serif text-xl mt-1">{fmtINR(v || 0)}</p></div>
        ))}
      </div>
      <div className="bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-blush/30"><tr><th className="text-left p-3">Order</th><th className="text-left p-3">Delivered</th><th className="text-right p-3">Value</th><th className="text-left p-3">Payout</th></tr></thead>
          <tbody>
            {delivered.length === 0 && <tr><td colSpan={4} className="text-center p-12 text-brand-muted">No delivered orders yet. Your COD payouts will appear here after delivery.</td></tr>}
            {delivered.map((o) => (
              <tr key={o.id} className="border-t border-brand-line">
                <td className="p-3 font-medium">{o.order_number}</td>
                <td className="p-3 text-brand-muted text-xs">{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                <td className="p-3 text-right">{fmtINR(o.total)}</td>
                <td className="p-3"><span className="jp-badge">Pending</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BulkUpload({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const downloadTemplate = async () => {
    try {
      const r = await api.get("/seller/products/csv-template", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = "joypearl_products_template.csv"; a.click();
    } catch { toast.error("Couldn't download template"); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/seller/products/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data); if (data.inserted) toast.success(`${data.inserted} product(s) uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <div className="bg-white border border-brand-line p-6 mb-6" data-testid="bulk-upload-form">
      <div className="flex justify-between items-start mb-4">
        <h3 className="font-serif text-xl">Bulk Product Upload</h3>
        <button onClick={downloadTemplate} className="jp-btn-outline text-xs px-3 py-2" data-testid="csv-template-btn"><Download size={14} className="mr-1"/>Download template</button>
      </div>
      <p className="text-sm text-brand-muted mb-4">Upload a CSV with columns: title, description, category_slug, mrp, price, stock, hsn_code, gst_rate, color, size. Products are marked pending until admin approval.</p>
      <form onSubmit={submit} className="space-y-3">
        <input type="file" accept=".csv" required onChange={(e) => setFile(e.target.files?.[0])} className="jp-input" data-testid="bulk-file"/>
        <div className="flex gap-3">
          <button type="submit" disabled={!file || uploading} className="jp-btn-primary" data-testid="bulk-submit">{uploading ? "Uploading…" : "Upload CSV"}</button>
          <button type="button" onClick={() => { onClose(); if (result?.inserted) onDone(); }} className="jp-btn-outline">Close</button>
        </div>
      </form>
      {result && (
        <div className="mt-4 text-sm" data-testid="bulk-result">
          <p className="text-emerald-700">✓ {result.inserted} inserted</p>
          {result.skipped?.length > 0 && (
            <div className="mt-2">
              <p className="text-red-700">⚠ {result.skipped.length} skipped:</p>
              <ul className="text-xs text-brand-muted mt-1">
                {result.skipped.slice(0, 10).map((s, i) => <li key={i}>Row {s.row}: {s.reason}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

